import { InstitutionMark } from "@components/InstitutionMark"
import { Badge } from "@components/ui/Badge"
import { RowActions, type RowAction } from "@components/ui/RowActions"
import { Table } from "@components/ui/Table"
import { formatNumber, formatRelative } from "@lib/intl"
import { ArrowRight, ListChecks, RotateCcw, Trash2 } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { Institution } from "../types"

import { ROUTES } from "@/constants/routes"

interface Props {
  institutions: Institution[]
  caption: string
  stale?: boolean
  /** The institution the whole console is looking at (header scope). */
  activeId: string
  /** An unfinished wizard run, if one belongs to a row. */
  draftInstitutionId: string | null
  onOpen: (institution: Institution) => void
  onContinueDraft: () => void
  onTrash: (institution: Institution) => void
  onRestore: (institution: Institution) => void
}

/**
 * Institutions as the same table as every other console list (PRD-60 chunk
 * D). They were cards with a generic building icon — the crest the edit
 * dialog manages never showed — and a red "Eliminar" on every card,
 * including the institution that houses Platonica's own staff.
 */
export const InstitutionsTable = memo(function InstitutionsTable({
  institutions,
  caption,
  stale,
  activeId,
  draftInstitutionId,
  onOpen,
  onContinueDraft,
  onTrash,
  onRestore,
}: Props) {
  const { t } = useTranslation(["institutions", "common"])

  function actionsFor(inst: Institution) {
    if (inst.deleted_at) {
      return (
        <RowActions
          subject={inst.name}
          primary={{
            label: t("actions.restore"),
            icon: <RotateCcw className="h-4 w-4" />,
            onClick: () => onRestore(inst),
          }}
        />
      )
    }
    const open: RowAction = {
      label: t("actions.open"),
      icon: <ArrowRight className="h-4 w-4" />,
      onClick: () => onOpen(inst),
    }
    // The staff's own institution: the API refuses to offboard it.
    const trash: RowAction[] = inst.is_internal
      ? []
      : [
          {
            label: t("actions.trash"),
            icon: <Trash2 className="h-4 w-4" />,
            onClick: () => onTrash(inst),
            danger: true,
          },
        ]
    if (inst.id === draftInstitutionId) {
      return (
        <RowActions
          subject={inst.name}
          primary={{
            label: t("actions.continue_onboarding"),
            icon: <ListChecks className="h-4 w-4" />,
            onClick: onContinueDraft,
          }}
          items={[open, ...trash]}
        />
      )
    }
    return <RowActions subject={inst.name} primary={open} items={trash} />
  }

  return (
    <Table
      caption={caption}
      rows={institutions}
      rowKey={(i) => i.id}
      identity={{
        header: t("fields.institution"),
        cell: (inst) => (
          <span className="flex min-w-0 items-center gap-3">
            <InstitutionMark name={inst.name} logoUrl={inst.logo_url} />
            <span className="min-w-0">
              {/* The name opens the customer's page (PRD-56 §FR-10); "Abrir"
                  switches the whole console to it. */}
              <Link
                to={ROUTES.institutionFor(inst.id)}
                className="text-ink block truncate font-medium underline-offset-2 hover:underline"
              >
                {inst.name}
              </Link>
              <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-meta">{t(`kind.${inst.kind}`)}</span>
                {inst.deleted_at ? (
                  <>
                    <Badge variant="neutral">{t("common:badge.deleted")}</Badge>
                    <span className="text-meta">
                      {t("common:account_status.deleted_when", {
                        when: formatRelative(inst.deleted_at),
                      })}
                    </span>
                  </>
                ) : (
                  <>
                    {inst.id === activeId && <Badge variant="accent">{t("badge.active")}</Badge>}
                    {inst.id === draftInstitutionId && (
                      <Badge variant="attention">{t("badge.onboarding")}</Badge>
                    )}
                    {inst.is_internal && <Badge variant="outline">{t("badge.internal")}</Badge>}
                  </>
                )}
              </span>
            </span>
          </span>
        ),
      }}
      columns={[
        {
          key: "cycle",
          header: t("fields.cycle"),
          cell: (inst) =>
            t("cycle_summary", {
              type: t(`common:academic.period_type.${inst.period_type}`),
              count: inst.periods_per_cycle,
              scale: t(`common:academic.grade_scale.${inst.grade_scale}`),
            }),
        },
        {
          key: "schools",
          header: t("fields.schools"),
          align: "end",
          cell: (inst) => formatNumber(inst.school_count),
        },
        {
          key: "professors",
          header: t("fields.professors"),
          align: "end",
          cell: (inst) => formatNumber(inst.professor_count),
        },
        {
          key: "admins",
          header: t("fields.admins"),
          align: "end",
          cell: (inst) => formatNumber(inst.admin_count),
        },
      ]}
      actions={actionsFor}
      actionsHeader={t("fields.actions")}
      stale={stale}
    />
  )
})
